import React from 'react';
import PageHeader from './PageHeader';
import { Card } from './DataToolbar';
import { Construction, Loader2, AlertTriangle, Inbox, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';

// Loading state
export function LoadingState({ label = 'Loading…' }) {
  return (
    <Card className="p-16 grid place-items-center animate-fade-in">
      <Loader2 className="h-6 w-6 text-teal-600 animate-spin" />
      <div className="mt-3 text-sm text-slate-500">{label}</div>
    </Card>
  );
}

// Error state
export function ErrorState({ error, onRetry }) {
  const message = typeof error === 'string' ? error : error?.message || 'Something went wrong.';
  return (
    <Card className="p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-xl bg-rose-50 text-rose-600 grid place-items-center mb-3">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <div className="font-semibold text-slate-900">Could not load data</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{message}</div>
      {onRetry && <Button onClick={onRetry} variant="outline" className="mt-4"><RotateCw className="h-4 w-4 mr-1.5" /> Retry</Button>}
    </Card>
  );
}

// Empty state
export function EmptyResults({ icon: Icon = Inbox, title = 'Nothing to show', description }) {
  return (
    <Card className="p-16 text-center">
      <div className="mx-auto h-12 w-12 rounded-xl bg-slate-100 text-slate-500 grid place-items-center mb-3"><Icon className="h-6 w-6" /></div>
      <div className="font-semibold text-slate-900">{title}</div>
      {description && <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{description}</div>}
    </Card>
  );
}

// Full page "pending / coming soon" state used for modules whose backend endpoints aren't ready yet
export default function PendingPage({ title, description, note }) {
  return (
    <div>
      <PageHeader
        title={title}
        description={description}
        actions={<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 text-[11px] font-semibold border border-amber-200">
          <Construction className="h-3 w-3" strokeWidth={2.5} /> Pending backend
        </span>}
      />
      <Card className="p-16 text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-700 grid place-items-center mb-4 ring-1 ring-amber-100">
          <Construction className="h-7 w-7" strokeWidth={1.75} />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 tracking-tight">Module under development</h3>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
          {note || `The backend endpoints for this module aren’t available yet. Once the API team ships them, this page will light up automatically.`}
        </p>
      </Card>
    </div>
  );
}
